import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Filter, Plus, Search } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { useBasesComerciais } from '../hooks';
import type {
  ListBasesComerciaisParams,
  MoedaBaseComercial,
  SituacaoBaseComercial,
} from '../api';
import { ReajustePendenteBadge, SituacaoBadge } from '../components/BaseComercialBadges';
import { SITUACAO_LABEL, formatCurrency, formatVigencia } from '../utils/format';

const PAGE_SIZE = 25;

const SITUACAO_OPTIONS = (Object.keys(SITUACAO_LABEL) as SituacaoBaseComercial[]).map((s) => ({
  value: s,
  label: SITUACAO_LABEL[s],
}));

const MOEDA_OPTIONS: { value: MoedaBaseComercial; label: string }[] = [
  { value: 'BRL', label: 'Real (BRL)' },
  { value: 'USD', label: 'Dólar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
];

/**
 * Listagem de Bases Comerciais (US-COM-005, task 10.2).
 *
 * **Mostra uma linha por Base, não por versão.** O backend filtra pela
 * versão mais recente de cada grupo por padrão — o usuário pensa na
 * Base, e o histórico de versões tem tela própria. O toggle "todas as
 * versões" existe para auditoria.
 */
export default function BasesComerciaisPage(): React.ReactElement {
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<string>('');
  const [modalidade, setModalidade] = useState('');
  const [moeda, setMoeda] = useState<string>('');
  const [somentePendentes, setSomentePendentes] = useState(false);
  const [todasAsVersoes, setTodasAsVersoes] = useState(false);
  const [page, setPage] = useState(0);

  const params: ListBasesComerciaisParams = useMemo(
    () => ({
      situacao: (situacao || undefined) as SituacaoBaseComercial | undefined,
      modalidade: modalidade || undefined,
      moeda: (moeda || undefined) as MoedaBaseComercial | undefined,
      reajustePendente: somentePendentes ? true : undefined,
      todasAsVersoes: todasAsVersoes ? true : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    }),
    [situacao, modalidade, moeda, somentePendentes, todasAsVersoes, page],
  );

  const { data, isLoading, isError, error } = useBasesComerciais(params);

  /**
   * A busca por nome/código é client-side sobre a página corrente: o
   * backend ainda não expõe `search` para Base Comercial. Filtrar aqui
   * evita prometer uma busca global que não existe — o rótulo diz
   * "nesta página".
   */
  const linhas = useMemo(() => {
    const base = data?.data ?? [];
    if (!busca.trim()) return base;
    const termo = busca.trim().toLowerCase();
    return base.filter(
      (b) =>
        b.nome.toLowerCase().includes(termo) ||
        b.codigo.toLowerCase().includes(termo) ||
        b.modalidade.toLowerCase().includes(termo),
    );
  }, [data, busca]);

  const totalPaginas = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Bases Comerciais</h1>
          <p className="mt-1 text-sm text-gray-600">
            Condições comerciais negociadas por cliente, versionadas e auditáveis.
          </p>
        </div>
        <div className="flex gap-2">
          {can(FEATURES.baseComercialAprovacao, 'edit') && (
            <Button variant="secondary" onClick={() => navigate('/base-comercial/aprovacoes')}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Aprovações
            </Button>
          )}
          {can(FEATURES.baseComercial, 'create') && (
            <Button onClick={() => navigate('/base-comercial/nova')}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Base
            </Button>
          )}
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextInput
          label="Buscar nesta página"
          icon={<Search className="h-4 w-4" />}
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <AutocompleteSelect
          label="Situação"
          icon={<Filter className="h-4 w-4" />}
          options={[{ value: '', label: 'Todas' }, ...SITUACAO_OPTIONS]}
          value={situacao}
          onChange={(v) => {
            setSituacao(v);
            setPage(0);
          }}
        />
        <TextInput
          label="Modalidade"
          value={modalidade}
          onChange={(e) => {
            setModalidade(e.target.value);
            setPage(0);
          }}
        />
        <AutocompleteSelect
          label="Moeda"
          options={[{ value: '', label: 'Todas' }, ...MOEDA_OPTIONS]}
          value={moeda}
          onChange={(v) => {
            setMoeda(v);
            setPage(0);
          }}
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2 text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={somentePendentes}
            onChange={(e) => {
              setSomentePendentes(e.target.checked);
              setPage(0);
            }}
          />
          Somente com reajuste pendente
        </label>
        <label className="flex items-center gap-2 text-gray-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300"
            checked={todasAsVersoes}
            onChange={(e) => {
              setTodasAsVersoes(e.target.checked);
              setPage(0);
            }}
          />
          Mostrar todas as versões
        </label>
      </div>

      {isError && (
        <Alert variant="error">
          Erro ao carregar Bases Comerciais: {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && !isError && linhas.length === 0 && (
        <EmptyState
          title="Nenhuma Base Comercial encontrada"
          description="Ajuste os filtros ou cadastre a primeira Base Comercial para este cliente."
        />
      )}

      {!isLoading && linhas.length > 0 && (
        <>
          {/* Scroll horizontal no mobile — a tabela tem 7 colunas e não
              cabe em 375px sem truncar informação relevante. */}
          <div className="-mx-4 overflow-x-auto sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Código</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Nome</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Modalidade</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Vigência</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Valor total</th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">Margem</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">Situação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {linhas.map((base) => (
                  <tr
                    key={base.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/base-comercial/${base.id}`)}
                  >
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-600">
                      {base.codigo}
                      <span className="ml-1 rounded bg-gray-100 px-1">v{base.versao}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-900">{base.nome}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{base.modalidade}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      {formatVigencia(base.dataInicio, base.dataFim)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-900">
                      {formatCurrency(base.valorTotal, base.moeda)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-gray-700">
                      {base.margemEstimadaPercentual
                        ? `${Number(base.margemEstimadaPercentual).toFixed(2)}%`
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <SituacaoBadge situacao={base.situacao} />
                        {base.reajustePendente && <ReajustePendenteBadge />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              {data?.total ?? 0} registro(s) · página {page + 1} de {totalPaginas}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page + 1 >= totalPaginas}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
